<template>
  <AnalyticsCard
    title="From visit to booking"
    subtitle="Distinct people reaching each step"
    :loading="loading"
    :empty="!loading && top === 0"
    empty-title="Nobody has visited yet"
    empty-hint="Share your booking link and the whole journey from arrival to booking shows up here."
    :height="300"
  >
    <template #actions>
      <span v-if="top > 0" class="text-xs text-gray-500 dark:text-gray-400">
        <b class="text-gray-900 dark:text-white tabular-nums">{{ overall }}%</b> overall
      </span>
    </template>

    <div class="p-5 space-y-1">
      <div v-for="(step, idx) in steps" :key="step.key">
        <div class="relative rounded-lg overflow-hidden bg-gray-50 dark:bg-white/[0.03]">
          <span class="absolute inset-y-0 left-0 transition-[width] duration-700"
            :style="{ width: `${width(step)}%`, backgroundColor: color(idx) }"></span>
          <div class="relative flex items-center justify-between gap-3 px-3.5 py-2.5">
            <span class="text-sm font-medium truncate"
              :class="width(step) > 55 ? 'text-white' : 'text-gray-800 dark:text-gray-100'">{{ step.label }}</span>
            <span class="flex items-baseline gap-2 flex-shrink-0">
              <span class="text-sm font-bold tabular-nums"
                :class="width(step) > 92 ? 'text-white' : 'text-gray-900 dark:text-white'">{{ step.count }}</span>
              <span class="text-[11px] tabular-nums w-9 text-right"
                :class="width(step) > 92 ? 'text-white/70' : 'text-gray-400 dark:text-gray-500'">
                {{ pct(step.share_of_top) }}
              </span>
            </span>
          </div>
        </div>

        <!-- The drop between two steps is the actionable part. A funnel that
             only draws the bars leaves the reader doing subtraction. -->
        <div v-if="idx < steps.length - 1" class="flex items-center gap-2 pl-3.5 py-1">
          <svg class="w-3 h-3 text-gray-300 dark:text-gray-600 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 5v14m0 0l-5-5m5 5l5-5"/>
          </svg>
          <span class="text-[11px] text-gray-400 dark:text-gray-500">
            <template v-if="steps[idx + 1].dropped > 0">
              <b class="text-amber-600 dark:text-amber-400 tabular-nums">{{ steps[idx + 1].dropped }}</b>
              {{ steps[idx + 1].dropped === 1 ? 'person left' : 'people left' }}
              &middot; {{ pct(steps[idx + 1].step_rate) }} carried on
            </template>
            <template v-else>Everyone carried on</template>
          </span>
        </div>
      </div>

      <!-- The one branch that is not a step. Someone who opened a calendar and
           found nothing bookable is a lost booking the funnel above cannot
           show, because they never reached a next step to fall out of. -->
      <div v-if="deadEndRate != null"
        class="!mt-4 flex items-start gap-2.5 px-3.5 py-2.5 rounded-lg bg-amber-50 dark:bg-amber-900/15 border border-amber-100 dark:border-amber-900/30">
        <svg class="w-4 h-4 text-amber-500 flex-shrink-0 mt-px" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/>
        </svg>
        <p class="text-xs text-amber-900 dark:text-amber-200 leading-relaxed">
          <b class="tabular-nums">{{ Math.round(deadEndRate * 100) }}%</b>
          of the people who opened your calendar found nothing they could book.
          <span class="text-amber-700 dark:text-amber-300/80">Widening your availability is the fix.</span>
        </p>
      </div>
    </div>
  </AnalyticsCard>
</template>

<script setup>
import { computed } from 'vue'
import AnalyticsCard from '../shared/AnalyticsCard.vue'
import { PALETTE, hexToRgba } from '../shared/useAnalyticsChart.js'

const props = defineProps({
  steps:       { type: Array, default: () => [] },
  deadEndRate: { type: Number, default: null },
  loading:     { type: Boolean, default: false },
})

const top = computed(() => props.steps[0]?.count || 0)
const overall = computed(() => {
  const last = props.steps[props.steps.length - 1]?.count || 0
  return top.value ? Math.round((last / top.value) * 100) : 0
})

// Floored so a step with a handful of people is still a visible bar rather
// than a hairline nobody can read a label against.
function width(step) {
  if (!top.value) return 0
  return Math.max(step.count > 0 ? 12 : 0, (step.count / top.value) * 100)
}

// Deepening blue down the funnel, so depth reads as progress rather than as
// five unrelated categories.
function color(idx) {
  return hexToRgba(PALETTE.blue, 0.35 + (idx / Math.max(1, props.steps.length - 1)) * 0.6)
}

function pct(value) {
  return value == null ? '—' : `${Math.round(value * 100)}%`
}
</script>
